import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { LANGS, OWNER_LABEL, ROLE_LABELS } from '@bingo/shared';
import { useAuth } from '../lib/auth';
import { Brand } from './Brand';
import { ThemeSwitch } from './ThemeSwitch';
import {
  IconAccount, IconBookings, IconCollapse, IconExpand, IconMoon, IconMore, IconPlanning, IconPower, IconTransfers, IconSettings, IconSun, IconToday, IconUsers,
} from './Icons';
import { RefProvider } from '../lib/reference';
import { useLang } from '../lib/i18n';
import { LangSwitch } from './LangSwitch';


const MORE_PATHS = ['/more', '/settings', '/users', '/account', '/import-history', '/integrations', '/bdr-settings'];

export function Shell() {
  const { me, logout, setTheme, setLanguage } = useAuth();
  const { pathname } = useLocation();
  const { lang, t } = useLang();
  // The menu always starts collapsed when the app is loaded (Bruno, 19/10); opening it lasts until the next load.
  const [collapsed, setCollapsed] = useState(true);
  const [, redraw] = useState(0);
  if (!me) return null;
  const toggleNav = () => setCollapsed((c) => !c);
  // Collapsed bar: one tap switches to the other look / the other language.
  const isDark = document.documentElement.dataset.theme === 'dark';
  const otherLang = LANGS.find((l) => l !== lang) ?? lang;
  const roleLabel = t(me.owner ? OWNER_LABEL : ROLE_LABELS[me.user.role]);
  // On the phone, everything that is not one of the four main screens lives under "More".
  const inMore = MORE_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
  const isDirection = me.user.role === 'direction';
  const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'active' : '');

  return (
    <div className={me.environment ? 'app-root has-env' : 'app-root'}>
      {me.environment && <div className="env-banner">{me.environment}: test data only</div>}
      {me.owner?.locked && (
        <div className="lock-banner">{t('Bingo App is locked for everyone else.')} <NavLink to="/account">{t('Unlock')}</NavLink></div>
      )}
      <div className={collapsed ? 'shell nav-collapsed' : 'shell'}>
        <aside className="side">
          <div className="side-head">
            <Brand />
            <button type="button" className="icon-btn side-toggle" onClick={toggleNav}
              aria-expanded={!collapsed} aria-label={t(collapsed ? 'Expand the menu' : 'Collapse the menu')} title={t(collapsed ? 'Expand the menu' : 'Collapse the menu')}>
              {collapsed ? <IconExpand /> : <IconCollapse />}
            </button>
          </div>
          <nav className="nav" aria-label={t('Main')}>
            {([
              ['/', <IconToday />, 'Today'],
              ['/bookings', <IconBookings />, 'Bookings'],
              ['/planning', <IconPlanning />, 'Planning'],
              ['/transfers', <IconTransfers />, 'Transfers'],
              // Office: the first four screens and My account only (Bruno, 19/10/2026).
              ...(isDirection ? [['/settings', <IconSettings />, 'Lists'] as const, ['/users', <IconUsers />, 'Users'] as const] : []),
              ['/account', <IconAccount />, 'My account'],
            ] as const).map(([to, icon, label]) => (
              <NavLink key={to} to={to} end={to === '/'} className={navClass} title={collapsed ? t(label) : undefined} aria-label={t(label)}>
                {icon}<span className="nav-label">{t(label)}</span>
              </NavLink>
            ))}
          </nav>
          {collapsed ? (
            <div className="side-foot side-foot-mini">
              <button type="button" className="icon-btn" onClick={() => { void setTheme(isDark ? 'light' : 'dark'); redraw((n) => n + 1); }}
                aria-label={t(isDark ? 'Switch to light mode' : 'Switch to dark mode')} title={t(isDark ? 'Switch to light mode' : 'Switch to dark mode')}>
                {isDark ? <IconMoon /> : <IconSun />}
              </button>
              <button type="button" className="icon-btn lang-btn" onClick={() => void setLanguage(otherLang)}
                aria-label={t('Language')} title={t('Language')}>
                {lang.toUpperCase()}
              </button>
              <div className="who-vertical" title={`${me.user.name} · ${roleLabel}`}>
                <b>{me.user.name}</b>
                <span className="muted">{roleLabel}</span>
              </div>
              <button type="button" className="icon-btn" onClick={logout} aria-label={t('Log out')} title={t('Log out')}>
                <IconPower />
              </button>
            </div>
          ) : (
            <div className="side-foot">
              <ThemeSwitch />
              <LangSwitch />
              <div className="who">
                <b>{me.user.name}</b>
                <span className="muted">{roleLabel}</span>
              </div>
              <button className="btn btn-sm" onClick={logout}>{t('Log out')}</button>
              <div className="version">v {me.version}</div>
            </div>
          )}
        </aside>

        <header className="topbar">
          <Brand />
        </header>

        <main className="main">
          <RefProvider>
            <Outlet />
          </RefProvider>
        </main>

        <nav className="tabbar" aria-label={t('Main')}>
          <NavLink to="/" end className={navClass}><IconToday />{t('Today')}</NavLink>
          <NavLink to="/bookings" className={navClass}><IconBookings />{t('Bookings')}</NavLink>
          <NavLink to="/planning" className={navClass}><IconPlanning />{t('Planning')}</NavLink>
          <NavLink to="/more" className={({ isActive }) => (isActive || inMore ? 'active' : '')}><IconMore />{t('More')}</NavLink>
        </nav>
      </div>
    </div>
  );
}
